'use client';
import React, { useRef } from 'react';
import Image from 'next/image';
import Button from '@/components/Button';
import Logo from '@/components/Logo';
import { useHeroAnimation } from './useHeroAnimation';
import { copyIconsHero } from '@/sections/develocity/HeroMainSection/heroMainSection.copy';
import LogosMarquee from '@/components/LogosMarquee';

interface IconProps {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
  className?: string;
  key?: string;
}

const renderIcon = ({ src, alt, width, height, className, key }: IconProps) => {
  return (
    <Logo
      key={`${key}-${alt}`}
      src={src}
      width={width}
      height={height}
      alt={alt}
      className={`absolute scale-25 transform opacity-80 md:scale-75 lg:scale-100 ${className}`}
    />
  );
};

const HeroMain = () => {
  const ref = useRef<HTMLDivElement>(null);
  useHeroAnimation(ref);

  return (
    <>
      <div className="flex flex-col overflow-hidden opacity-0" ref={ref}>
        <div className="z-1 flex w-full flex-col items-center justify-center bg-light-background-200 pb-12 text-center md:px-10 md:pb-14 lg:px-14 lg:pb-16 xl:pb-20">
          <div className="hero-content max-w-full pb-8 pt-28 text-center text-white md:pb-12 lg:pb-14">
            <div className={`laptop relative z-10 scale-900 transform overflow-hidden`}>
              <Image src="/assets/images/laptop.png" alt="Hero Main" width={945} height={543} className="z-10" />

              <div
                className={
                  'absolute left-1/2 top-2 -z-20 w-4/5 max-w-3xl -translate-x-1/2 transform overflow-hidden rounded-xl md:top-3 md:rounded-3xl lg:w-full'
                }
              >
                <video autoPlay loop muted height={'110%'} width={'100%'} playsInline>
                  {/* TODO: We have a compressed version of this asset. Can we use it? ask design. */}
                  <source src="/assets/videos/GRADLE_heroBG.mp4" type="video/mp4" />
                </video>
              </div>

              <div className="main-logo absolute left-1/2 top-1/2 w-4/12 -translate-x-1/2 -translate-y-1/2 transform md:top-40 md:w-auto md:-translate-y-0">
                <p className="title mb-4 text-body-xl text-caption-sm md:mb-4 md:text-eyebrow-md lg:mb-4 xl:mb-6">
                  Gradle Enterprise is now...
                </p>
                <Logo
                  src="/assets/images/develocity-logo.svg"
                  width={638.65}
                  height={62.48}
                  className="logo mb-2 md:mb-4 xl:mb-8"
                />
                <p className="subtitle text-caption-sm md:text-eyebrow-xl">
                  The leading <span className="text-gradle-blue">Developer Productivity</span> platform
                </p>
              </div>

              <div className={`absolute left-0 top-0 h-full w-full`}>
                {copyIconsHero.map((icon, index) =>
                  renderIcon({
                    ...icon,
                    key: `icon-${index}`,
                  }),
                )}
              </div>
            </div>
            <div className="pointer-events-none absolute left-1/2 top-1/3 z-20 flex h-full w-full -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center">
              <Image
                src="/assets/images/icon/develocity-D.svg"
                width={100}
                height={100}
                alt="logo"
                className="icon-logo mb-6"
              />
              <h1 className="text-headline-bold">
                <p>Build better</p>
                <p>software, faster</p>
              </h1>
            </div>
          </div>
          {/* TODO : Change with Hero Component */}
          <div className="main-text mb-8 max-w-sm text-center md:max-w-4xl">
            <p className="title text-h1 mb-4 text-gray-1">Get back one day a week</p>
            <p className="subtitle text-body md:text-eyebrow text-gray-2">
              Develocity® speeds up your builds and tests, makes troubleshooting more efficient, and increases
              toolchain reliability, giving your developers back at least one day each week in lost productivity. Now
              supporting Maven, sbt, and Gradle build systems.
            </p>
          </div>
          <Button className="button" href="/develocity/product-tour/home/" variant="primary">
            Tour the product
          </Button>
        </div>
      </div>

      <section className="flex flex-col gap-8 py-8 lg:py-10 xl:py-10">
        <div className="text-center">
          <h5 className="text-eyebrow-strong p-0 text-gray-1">Used by leading tech and global business brands</h5>
        </div>
        <LogosMarquee blurToColor="white" />
      </section>
    </>
  );
};

export default HeroMain;
